import {
   AlertDialog,
   AlertDialogBody,
   AlertDialogContent,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogOverlay,
   Button,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { useLocalization } from '~/framework';

export type ConfirmationAlertProps = {
   isOpen: boolean;
   onCancel: () => void;
   onConfirm: () => void;
   closeOnOverlayClick?: boolean;
   title?: string;
   message?: string;
   cancelDataFeatureId?: string;
   confirmDataFeatureId?: string;
   cancelActionName?: string;
   confirmActionName?: string;
};

/**
 * An alert component that requires user explicit confirmation before proceeding with the action.
 */
export const ConfirmationAlert = ({
   isOpen,
   onCancel,
   onConfirm,
   closeOnOverlayClick = false,
   title,
   message,
   cancelDataFeatureId,
   confirmDataFeatureId,
   cancelActionName,
   confirmActionName,
}: ConfirmationAlertProps) => {
   const { _js } = useLocalization();
   const cancelRef = useRef<HTMLButtonElement>(null);

   return (
      <AlertDialog
         isOpen={isOpen}
         leastDestructiveRef={cancelRef}
         onClose={onCancel}
         closeOnOverlayClick={closeOnOverlayClick}
      >
         <AlertDialogOverlay />
         <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
               {title ?? _js('Confirmation needed')}
            </AlertDialogHeader>
            <AlertDialogBody>
               {message ?? _js('Are you sure you want to go ahead with this action?')}
            </AlertDialogBody>
            <AlertDialogFooter>
               <Button data-feature-id={cancelDataFeatureId} ref={cancelRef} onClick={onCancel}>
                  {cancelActionName ?? _js('Cancel')}
               </Button>
               <Button
                  data-feature-id={confirmDataFeatureId}
                  colorScheme="red"
                  onClick={onConfirm}
                  ml={3}
                  variant="solid"
               >
                  {confirmActionName ?? _js('Confirm')}
               </Button>
            </AlertDialogFooter>
         </AlertDialogContent>
      </AlertDialog>
   );
};
